import type { Document, DocumentUploadTarget } from '@proref/types';
import type { CreateDocumentInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

// Documents visible to the caller (platform + own-federation, sport-scoped). super_admin may
// narrow to one sport via `sportId`; referees/federation_admins get their own sport regardless.
export async function listDocuments(sportId?: string): Promise<Document[]> {
  const res = await api.get<Envelope<Document[]>>('/documents', {
    params: sportId ? { sport_id: sportId } : undefined,
  });
  return unwrap(res.data);
}

// Step 1 of an admin upload: create the row + get a presigned PUT (bytes go straight to S3).
export async function createDocumentUploadUrl(input: CreateDocumentInput): Promise<DocumentUploadTarget> {
  const res = await api.post<Envelope<DocumentUploadTarget>>('/documents/upload-url', input);
  return unwrap(res.data);
}

// Step 2: PUT the file to the presigned URL. Content-Type must match what was pinned into the
// signature (pdf/docx), or S3 rejects it.
export async function uploadDocumentToS3(url: string, file: File, contentType: string): Promise<void> {
  const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`S3 upload failed (HTTP ${res.status})${body ? ` — ${body.slice(0, 200)}` : ''}`);
  }
}

// Resolve a short-lived signed download URL for a document, then the caller opens it.
export async function getDocumentDownloadUrl(id: string): Promise<string> {
  const res = await api.get<Envelope<{ url: string }>>(`/documents/${id}/download`);
  return unwrap(res.data).url;
}

export async function deleteDocument(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/documents/${id}`);
}
